import { Action } from '../types/types';
import {
	SET_ACTIVE_TAB,
	SET_ACTIVE_SUB_TAB,
	SET_META_MENU_ROUTE,
	SET_META_MENU_STATE,
	SET_META_MENU_OPTION,
	CLOSE_META_MENU,
	SET_RIGHT_DRAWER_OPEN,
} from '../actions/tabs';
import { TABS, META_MENU_ROUTES, META_MENU_STATES, MetaMenuOptions, SUB_TABS_MAP } from '../types/tabs';
import type { TabsState } from '../types/tabs';

export type { TabsState } from '../types/tabs';
import {
	MEDIA_SETTINGS_TABS,
	MediaTabOptionTypes,
} from '../types/session';
import { AudienceDrawerOptionTypes } from '../../components/right-panel/audience/audience-drawer';

type ActiveSubTabAreas = 'media'
interface SetSubtabAction extends Action {
	payload: {
		tab: ActiveSubTabAreas
		subTab: string
	};
}

export const initialTabsState: TabsState = {
	rightDrawerOpen: false,
	previousTab: TABS.NONE,
	activeTab: TABS.NONE,
	previousActiveTab: TABS.NONE,
	activeSubTabs: {
		[TABS.MEDIA]: MediaTabOptionTypes.Files,
		[TABS.AUDIENCE]: AudienceDrawerOptionTypes.Chat,
		[TABS.GENERATE_MEDIA_CONTENT]: MEDIA_SETTINGS_TABS.CONTENT,
	},
	metaMenuActiveRoute: META_MENU_ROUTES.NONE,
	metaMenuState: META_MENU_STATES.CLOSED,
	metaMenuActiveOption: MetaMenuOptions.Info,
	lastMetaMenuRoute: META_MENU_ROUTES.NONE,
}

const actions: Map<string, Function> = new Map([
	[SET_ACTIVE_TAB, (state: TabsState, action: Action) => {

		// Handle closing a SUB_TAB (e.g., GENERATE_MEDIA_CONTENT, OVERLAYS)
		if (
			SUB_TABS_MAP[state.activeTab]
			&& action.payload === TABS.NONE
		) {
			const hasPreviousTab = state.previousTab && !SUB_TABS_MAP[state.previousTab] && state.previousTab !== TABS.NONE;
			const hasLastMetaMenuRoute = state.lastMetaMenuRoute && state.lastMetaMenuRoute !== META_MENU_ROUTES.NONE;

			// If meta menu is currently open or closing, stay on current tab
			if (state.metaMenuState !== META_MENU_STATES.CLOSED) {
				return {
					...state,
					activeTab: !hasPreviousTab && state.activeTab === TABS.OVERLAYS ? TABS.FEEDS : state.previousTab,
					rightDrawerOpen: true,
				};
			}

			// If we have a saved meta menu route, restore it instead of changing tabs
			if (hasLastMetaMenuRoute) {
				return {
					...state,
					activeTab: hasPreviousTab && state.activeTab !== state.previousTab ? state.previousTab : TABS.FEEDS,
					previousTab: state.activeTab,
					metaMenuActiveRoute: state.lastMetaMenuRoute,
					metaMenuState: META_MENU_STATES.OPENING,
					lastMetaMenuRoute: META_MENU_ROUTES.NONE,
					// Keep current tab active while meta menu opens
				};
			}

			// Otherwise, go to previous valid tab or default to FEEDS
			if (hasPreviousTab) {
				action.payload = state.previousTab;
			} else {
				action.payload = TABS.FEEDS;
			}
		}

		const newState = {
			...state,
			activeTab: action.payload,
			previousTab: state.activeTab,
			rightDrawerOpen: action.payload !== TABS.NONE
		};

		// If switching to a non-SUB_TAB (and not NONE), clear meta menu tracking
		// This ensures we don't try to restore meta menu when user has moved on
		if (action.payload !== TABS.NONE && !SUB_TABS_MAP[action.payload]) {
			newState.lastMetaMenuRoute = META_MENU_ROUTES.NONE;
		}

		return newState;
	}],
	[SET_ACTIVE_SUB_TAB, (state: TabsState, action: SetSubtabAction) => {
		const newActiveSubTabs = { ...state.activeSubTabs }; // clone it
		newActiveSubTabs[action.payload.tab] = action.payload.subTab;
		return {
			...state,
			activeSubTabs: newActiveSubTabs,
		};
	}],
	[SET_META_MENU_ROUTE, (state: TabsState, action: Action) => {
		const newState = { ...state}
		// if our action is a different route than the current
		if (action.payload !== newState.metaMenuActiveRoute) {
			// save the last active route
			newState.lastMetaMenuRoute = state.metaMenuActiveRoute;
			// set new active route
			newState.metaMenuActiveRoute = action.payload;
		}

		return newState;
	}],
	[SET_META_MENU_STATE, (state: TabsState, action: Action) => {
		const {
			OPENING,
			OPEN,
			CLOSING,
			CLOSED,
		} = META_MENU_STATES;

		const alreadyOpen = action.payload === OPENING && state.metaMenuState === OPEN;
		const alreadyClosed = action.payload === CLOSING && state.metaMenuState === CLOSED;

		if (alreadyOpen || alreadyClosed) {
			return { ...state };
		}

		return { ...state, metaMenuState: action.payload };
	}],
	[SET_META_MENU_OPTION, (state: TabsState, action: Action) => (
		{ ...state, metaMenuActiveOption: action.payload }
	)],
	[SET_RIGHT_DRAWER_OPEN, (state: TabsState, action: Action) => (
		{ ...state,rightDrawerOpen: action.payload }
	)],
	[CLOSE_META_MENU, (state: TabsState) => ({ 
		...state,
		lastMetaMenuRoute: state.metaMenuActiveRoute,
		metaMenuActiveRoute: META_MENU_ROUTES.NONE,
		metaMenuState: state.metaMenuState === META_MENU_STATES.CLOSED ? META_MENU_STATES.CLOSED : META_MENU_STATES.CLOSING,
		metaMenuActiveOption: MetaMenuOptions.Info,
		rightDrawerOpen: state.activeTab === TABS.NONE ? false : state.rightDrawerOpen,
	})]
]);

// eslint-disable-next-line default-param-last
export function TabsReducer(state: TabsState = initialTabsState, action: Action) {
	return actions.get(action.type)?.(state, action) || state;
}
